import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Home } from 'lucide-react';
import SignaturePad from '../components/SignaturePad';

export default function DSPApplicationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    ssn: '',
    address: '',
    city: '',
    state: 'NC',
    zip: '',
    // Employment
    availability: '',
    startDate: '',
    // Work Experience
    employer1: '',
    employer1DatesFrom: '',
    employer1DatesTo: '',
    employer1Duties: '',
    employer2: '',
    employer2DatesFrom: '',
    employer2DatesTo: '',
    employer2Duties: '',
    // Role Understanding Checkboxes
    understandNonHandsOn: false,
    understandAuthorizedServices: false,
    // Mandatory Consents
    consentBackgroundCheck: false,
    consentRegistryCheck: false,
    consentSupervision: false,
    consentTraining: false,
    // Additional
    references: ''
  });

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate signature
    if (!signatureData) {
      toast.error('Please sign the application before submitting.');
      return;
    }
    
    // Validate Role Understanding checkboxes
    if (!formData.understandNonHandsOn || !formData.understandAuthorizedServices) {
      toast.error('Please acknowledge all Role-Specific Understanding items.');
      return;
    }
    
    // Validate Mandatory Consents
    if (!formData.consentBackgroundCheck || !formData.consentRegistryCheck || 
        !formData.consentSupervision || !formData.consentTraining) {
      toast.error('Please acknowledge all Mandatory Consents & Acknowledgments.');
      return;
    }
    
    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('position_type', 'dsp');
      formDataToSend.append('first_name', formData.firstName);
      formDataToSend.append('middle_name', formData.middleName || '');
      formDataToSend.append('last_name', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('ssn', formData.ssn || '');
      formDataToSend.append('address', formData.address);
      formDataToSend.append('city', formData.city);
      formDataToSend.append('state', formData.state);
      formDataToSend.append('zip_code', formData.zip);
      formDataToSend.append('availability', formData.availability);
      formDataToSend.append('start_date', formData.startDate);
      // Work Experience
      formDataToSend.append('employer_1', formData.employer1 || '');
      formDataToSend.append('employer_1_dates_from', formData.employer1DatesFrom || '');
      formDataToSend.append('employer_1_dates_to', formData.employer1DatesTo || '');
      formDataToSend.append('key_duties_1', formData.employer1Duties || '');
      formDataToSend.append('employer_2', formData.employer2 || '');
      formDataToSend.append('employer_2_dates_from', formData.employer2DatesFrom || '');
      formDataToSend.append('employer_2_dates_to', formData.employer2DatesTo || '');
      formDataToSend.append('key_duties_2', formData.employer2Duties || '');
      // Consents (as JSON string)
      formDataToSend.append('consents', JSON.stringify({
        understand_non_hands_on: formData.understandNonHandsOn,
        understand_authorized_services: formData.understandAuthorizedServices,
        consent_background_check: formData.consentBackgroundCheck,
        consent_registry_check: formData.consentRegistryCheck,
        consent_supervision: formData.consentSupervision,
        consent_training: formData.consentTraining
      }));
      formDataToSend.append('references', formData.references || '');
      formDataToSend.append('signature_data', signatureData);
      
      if (resumeFile) {
        formDataToSend.append('resume', resumeFile);
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/applications/submit`, {
        method: 'POST',
        body: formDataToSend
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit application');
      }
      
      toast.success('Application submitted successfully! We will contact you soon.');
      navigate('/careers');
    } catch (error) {
      toast.error(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-purple-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-purple-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_caregiver-command/artifacts/prj1fr46_Integrity%20Home%20Care%20Logo%20Webpage.png"
                alt="Integrity Home Care LLC"
                className="h-16 w-auto"
              />
            </div>
            <Button
              onClick={() => navigate('/careers')}
              variant="outline"
              className="border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Careers
            </Button>
          </div>
        </div>
      </nav>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-purple-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            DSP (Direct Support Professional) Application
          </h1>
          <p className="text-lg text-gray-700">
            Companion Sitter Position - Join our team of compassionate caregivers
          </p>
        </div>

        <Card className="p-8 border-purple-100">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-6">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label>Middle Name</Label>
                  <Input
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label>Social Security Number *</Label>
                <Input
                  type="text"
                  name="ssn"
                  value={formData.ssn}
                  onChange={handleChange}
                  placeholder="XXX-XX-XXXX"
                  required
                  maxLength={11}
                />
                <p className="text-xs text-gray-500 mt-1">Required for background check and employment verification</p>
              </div>

              <div className="mt-4">
                <Label>Street Address *</Label>
                <Input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <Label>City *</Label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label>State *</Label>
                  <Input
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label>ZIP Code *</Label>
                  <Input
                    name="zip"
                    value={formData.zip}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Role-Specific Understanding */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-4">II. Role-Specific Understanding</h2>
              <p className="text-gray-600 mb-4">
                Companion Sitter services are "Non-Hands-On." By checking the boxes below, you acknowledge that you understand the scope of this role:
              </p>
              <div className="space-y-4 bg-purple-50 p-4 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.understandNonHandsOn}
                    onChange={(e) => setFormData({...formData, understandNonHandsOn: e.target.checked})}
                    className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    required
                  />
                  <span className="text-gray-700">
                    I understand that I <strong>cannot</strong> provide "hands-on" care, which includes bathing, dressing, toileting, or physical assistance with transfers.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.understandAuthorizedServices}
                    onChange={(e) => setFormData({...formData, understandAuthorizedServices: e.target.checked})}
                    className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    required
                  />
                  <span className="text-gray-700">
                    I am authorized to provide:
                    <ul className="list-disc ml-6 mt-2 space-y-1">
                      <li><strong>Home Management:</strong> Shopping, errands, and meal preparation.</li>
                      <li><strong>Socialization:</strong> Providing companionship and monitoring client safety.</li>
                      <li><strong>Medication Reminders:</strong> Reminding clients to take self-administered medications.</li>
                    </ul>
                  </span>
                </label>
              </div>
            </div>

            {/* Work Experience */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-4">III. Work Experience</h2>
              <p className="text-gray-600 mb-4">
                Please list previous experience in caregiving, hospitality, or community service.
              </p>
              
              {/* Employer 1 */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-gray-800 mb-3">Employer 1 (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Employer Name</Label>
                    <Input
                      name="employer1"
                      value={formData.employer1}
                      onChange={handleChange}
                      placeholder="Company or Organization"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>From</Label>
                      <Input
                        type="date"
                        name="employer1DatesFrom"
                        value={formData.employer1DatesFrom}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label>To</Label>
                      <Input
                        type="date"
                        name="employer1DatesTo"
                        value={formData.employer1DatesTo}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Key Duties</Label>
                  <Textarea
                    name="employer1Duties"
                    value={formData.employer1Duties}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Describe your responsibilities..."
                  />
                </div>
              </div>

              {/* Employer 2 */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3">Employer 2 (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Employer Name</Label>
                    <Input
                      name="employer2"
                      value={formData.employer2}
                      onChange={handleChange}
                      placeholder="Company or Organization"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>From</Label>
                      <Input
                        type="date"
                        name="employer2DatesFrom"
                        value={formData.employer2DatesFrom}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label>To</Label>
                      <Input
                        type="date"
                        name="employer2DatesTo"
                        value={formData.employer2DatesTo}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Key Duties</Label>
                  <Textarea
                    name="employer2Duties"
                    value={formData.employer2Duties}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Describe your responsibilities..."
                  />
                </div>
              </div>
            </div>

            {/* Mandatory Consents */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-4">IV. Mandatory Consents & Acknowledgments</h2>
              <div className="space-y-4 bg-amber-50 p-4 rounded-lg border border-amber-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.consentBackgroundCheck}
                    onChange={(e) => setFormData({...formData, consentBackgroundCheck: e.target.checked})}
                    className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    required
                  />
                  <span className="text-gray-700">
                    <strong>Background Check:</strong> I consent to a criminal history record check for all states of residence for the past five (5) years.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.consentRegistryCheck}
                    onChange={(e) => setFormData({...formData, consentRegistryCheck: e.target.checked})}
                    className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    required
                  />
                  <span className="text-gray-700">
                    <strong>Registry Check:</strong> I authorize a pre-employment check of the Health Care Personnel Registry (HCPR). I understand that any substantiated findings of abuse or neglect will disqualify me from employment.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.consentSupervision}
                    onChange={(e) => setFormData({...formData, consentSupervision: e.target.checked})}
                    className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    required
                  />
                  <span className="text-gray-700">
                    <strong>Supervision:</strong> I understand that an agency supervisor will contact the client receiving care every three months and make a supervisory visit to the residence at least every six months to monitor the quality of services I provide.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.consentTraining}
                    onChange={(e) => setFormData({...formData, consentTraining: e.target.checked})}
                    className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    required
                  />
                  <span className="text-gray-700">
                    <strong>Training & Orientation:</strong> I agree to attend orientation, which will include training on Client Rights and agency infection control policies.
                  </span>
                </label>
              </div>
            </div>

            {/* Availability Section */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-6">V. Availability & Start Date</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Availability *</Label>
                  <select
                    name="availability"
                    value={formData.availability}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select availability</option>
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="weekend">Weekends only</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
                <div>
                  <Label>Available Start Date *</Label>
                  <Input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* References and Resume */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-6">VI. References & Documents</h2>
              <div className="space-y-4">
                <div>
                  <Label>References (Name, Relationship, Phone Number)</Label>
                  <Textarea
                    name="references"
                    value={formData.references}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Please provide at least two professional references..."
                  />
                </div>

                <div>
                  <Label>Upload Resume or CV *</Label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setResumeFile(e.target.files[0])}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-sm text-gray-600 mt-2">Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
                </div>
              </div>
            </div>

            {/* Signature Section */}
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-purple-900 flex items-center gap-2">
                  <span className="p-2 bg-purple-100 rounded-full">
                    <Heart className="w-5 h-5 text-purple-600" />
                  </span>
                  Signature & Agreement
                </h2>
                <p className="text-gray-600 mt-2">
                  Please read the agreement below and provide your electronic signature to complete your application.
                </p>
              </div>
              
              <SignaturePad 
                onSignatureChange={setSignatureData}
                label="Applicant Signature"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-purple-100">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg"
                data-testid="submit-application-button"
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
              <p className="text-sm text-gray-600 text-center mt-4">
                By submitting this application, you acknowledge that all information provided is accurate and complete.
              </p>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}